import { useState } from 'react'
import { 
  Plus, Save, Trash2, Image as ImageIcon, AlertCircle,
  CheckCircle, Upload, Download, Calendar, Tag, BookOpen,
  Zap, Filter, Eye, Edit2
} from 'lucide-react'

export default function QuestionManager() {
  const [questions, setQuestions] = useState([])
  const [editingQuestion, setEditingQuestion] = useState(null)
  const [showForm, setShowForm] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    type: 'general-knowledge',
    difficulty: 'medium',
    text: '',
    options: ['', '', '', ''],
    answer: '',
    explanation: '',
    imageUrl: '',
    newsDate: '',
    newsContext: '',
    learnMore: '',
    tags: [],
    isNew: true
  })

  const [newTag, setNewTag] = useState('')
  const [importJSON, setImportJSON] = useState('')
  const [message, setMessage] = useState({ type: '', text: '' })

  const generateQuestionId = (type) => {
    const prefix = type === 'current-affairs' ? 'oir_ca' :
                   type === 'non-verbal' ? 'oir_nv' :
                   type === 'verbal' ? 'oir_verbal' : 'oir'
    const timestamp = Date.now().toString().slice(-6)
    return `${prefix}_${timestamp}`
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options]
    newOptions[index] = value
    setFormData(prev => ({ ...prev, options: newOptions }))
  }

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validation
    if (!formData.text || formData.options.some(opt => !opt) || !formData.answer || !formData.explanation) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' })
      return
    }

    if (!formData.options.includes(formData.answer)) {
      setMessage({ type: 'error', text: 'Answer must be one of the options' })
      return
    }

    const questionData = {
      ...formData,
      id: formData.id || generateQuestionId(formData.type)
    }

    if (editingQuestion) {
      setQuestions(prev => prev.map(q => q.id === editingQuestion.id ? questionData : q))
      setMessage({ type: 'success', text: 'Question updated successfully!' })
    } else {
      setQuestions(prev => [...prev, questionData])
      setMessage({ type: 'success', text: 'Question added successfully!' })
    }

    resetForm()
  }

  const resetForm = () => {
    setFormData({
      id: '',
      type: 'general-knowledge',
      difficulty: 'medium',
      text: '',
      options: ['', '', '', ''],
      answer: '',
      explanation: '',
      imageUrl: '',
      newsDate: '',
      newsContext: '',
      learnMore: '',
      tags: [],
      isNew: true
    })
    setEditingQuestion(null)
    setShowForm(false)
  }

  const handleEdit = (question) => {
    setFormData(question)
    setEditingQuestion(question)
    setShowForm(true)
  }

  const handleDelete = (id) => {
    if (confirm('Are you sure you want to delete this question?')) {
      setQuestions(prev => prev.filter(q => q.id !== id))
      setMessage({ type: 'success', text: 'Question deleted successfully!' })
    }
  }

  const handleExport = () => {
    const jsonData = {
      questions,
      metadata: {
        version: "2.0",
        lastUpdated: new Date().toISOString().split('T')[0],
        totalQuestions: questions.length,
        exportedBy: "Question Manager"
      }
    }
    
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `oir_questions_${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
    
    setMessage({ type: 'success', text: 'Questions exported successfully!' })
  }

  const handleImport = () => {
    try {
      const parsed = JSON.parse(importJSON)
      if (parsed.questions && Array.isArray(parsed.questions)) {
        setQuestions(prev => [...prev, ...parsed.questions])
        setImportJSON('')
        setMessage({ type: 'success', text: `Imported ${parsed.questions.length} questions!` })
      } else {
        setMessage({ type: 'error', text: 'Invalid JSON format' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to parse JSON: ' + err.message })
    }
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      // In a real app, you'd upload to a server or cloud storage
      // For now, we'll just show a placeholder URL
      const fakeUrl = `/assets/oir-images/${file.name}`
      setFormData(prev => ({ ...prev, imageUrl: fakeUrl }))
      setMessage({ type: 'info', text: `Image set: ${file.name}. Upload to /public/assets/oir-images/` })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BookOpen className="w-8 h-8 text-primary-600" />
            Question Manager
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Add, edit, and manage OIR practice questions
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`card animate-fadeIn ${
            message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500' :
            message.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500' :
            'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
          }`}>
            <div className="flex items-start gap-2">
              {message.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
               message.type === 'error' ? <AlertCircle className="w-5 h-5 text-red-600" /> :
               <AlertCircle className="w-5 h-5 text-blue-600" />}
              <p className={`text-sm ${
                message.type === 'success' ? 'text-green-900 dark:text-green-100' :
                message.type === 'error' ? 'text-red-900 dark:text-red-100' :
                'text-blue-900 dark:text-blue-100'
              }`}>
                {message.text}
              </p>
              <button
                onClick={() => setMessage({ type: '', text: '' })}
                className="ml-auto text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add New Question
          </button>

          <button
            onClick={handleExport}
            disabled={questions.length === 0}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export ({questions.length})
          </button>

          <div className="text-lg font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-600" />
            {questions.length} Questions
          </div>
        </div>
      </div>

      {/* Question Form */}
      {showForm && (
        <div className="card animate-fadeIn">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Edit2 className="w-5 h-5" />
            {editingQuestion ? 'Edit Question' : 'Add New Question'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type and Difficulty */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Question Type *
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                  required
                >
                  <option value="general-knowledge">General Knowledge</option>
                  <option value="verbal">Verbal Reasoning</option>
                  <option value="non-verbal">Non-Verbal Reasoning</option>
                  <option value="current-affairs">Current Affairs</option>
                  <option value="defence">Defence Related</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Difficulty *
                </label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                  required
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
            </div>

            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Question Text *
              </label>
              <textarea
                name="text"
                value={formData.text}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                rows="3"
                required
                placeholder="Enter the question..."
              />
            </div>

            {/* Image Upload (for non-verbal) */}
            {formData.type === 'non-verbal' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Question Image
                </label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                  />
                  <input
                    type="text"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleInputChange}
                    placeholder="Or enter image URL"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                  />
                </div>
                {formData.imageUrl && (
                  <img src={formData.imageUrl} alt="Preview" className="mt-2 max-h-40 rounded border" onError={(e) => e.target.style.display = 'none'} />
                )}
              </div>
            )}

            {/* Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Options * (4 required)
              </label>
              <div className="grid md:grid-cols-2 gap-3">
                {formData.options.map((option, index) => (
                  <input
                    key={index}
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                    required
                  />
                ))}
              </div>
            </div>

            {/* Correct Answer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Correct Answer *
              </label>
              <select
                name="answer"
                value={formData.answer}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                required
              >
                <option value="">Select correct answer</option>
                {formData.options.filter(opt => opt).map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* Explanation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Explanation *
              </label>
              <textarea
                name="explanation"
                value={formData.explanation}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                rows="3"
                required
                placeholder="Explain why this is the correct answer..."
              />
            </div>

            {/* Current Affairs Fields */}
            {formData.type === 'current-affairs' && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      News Date
                    </label>
                    <input
                      type="date"
                      name="newsDate"
                      value={formData.newsDate}
                      onChange={handleInputChange}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Learn More URL
                    </label>
                    <input
                      type="url"
                      name="learnMore"
                      value={formData.learnMore}
                      onChange={handleInputChange}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    News Context
                  </label>
                  <textarea
                    name="newsContext"
                    value={formData.newsContext}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                    rows="2"
                    placeholder="Brief context about the news..."
                  />
                </div>
              </>
            )}

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tags
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add tag and press Enter"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="btn-secondary"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-red-600"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Mark as New */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isNew"
                checked={formData.isNew}
                onChange={handleInputChange}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Mark as NEW (will show NEW badge)
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button type="submit" className="btn-primary flex items-center gap-2">
                <Save className="w-4 h-4" />
                {editingQuestion ? 'Update Question' : 'Save Question'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Import Section */}
      <div className="card">
        <h3 className="font-bold mb-3 flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Import Questions (JSON)
        </h3>
        <textarea
          value={importJSON}
          onChange={(e) => setImportJSON(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 font-mono text-sm"
          rows="5"
          placeholder='Paste JSON here: {"questions": [...]}'
        />
        <button
          onClick={handleImport}
          disabled={!importJSON.trim()}
          className="btn-primary mt-3 disabled:opacity-50"
        >
          Import
        </button>
      </div>

      {/* Questions List */}
      <div className="card">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5" />
          Saved Questions ({questions.length})
        </h3>
        
        {questions.length === 0 ? (
          <p className="text-center text-gray-600 dark:text-gray-400 py-8">
            No questions yet. Add your first question above!
          </p>
        ) : (
          <div className="space-y-3">
            {questions.map((q, index) => (
              <div key={q.id} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-mono text-gray-500">{q.id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        q.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                        q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {q.difficulty}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                        {q.type}
                      </span>
                      {q.isNew && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-gradient-to-r from-orange-400 to-pink-500 text-white">
                          NEW
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">{q.text}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <strong>Answer:</strong> {q.answer}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {q.tags.map(tag => (
                        <span key={tag} className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(q)}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(q.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
